import {Injectable, NgZone} from '@angular/core';
import {catchError, first, from, map, Observable, of} from "rxjs";
import {getToken, Messaging, onMessage} from "@angular/fire/messaging";
import {StorageService} from "../storage/storage.service";
import {StudentHelperService} from "../../../student/services/student-helper.service";
import {RedirectInfo} from '../../model/interface/helper';
import {BaseRoutes, StudentRoutes} from "../../model/enum/routes.enum";
import {Roles} from "../../model/enum/roles.enum";
import {LessonType} from "../../model/enum/lessons";
import {MessagePayload, Unsubscribe} from "@firebase/messaging";
import {PushNotificationData} from "../../model/interface/push-notifications.model";
import {TranslateService} from "@ngx-translate/core";
import {AuthService} from "../auth/auth.service";
import {ToastrService} from "ngx-toastr";
import {Router} from "@angular/router";
import {environment} from "../../../../environment";

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  private messageUnsubscribe: Unsubscribe | null = null;

  constructor(
    private readonly messaging: Messaging,
    private readonly storageService: StorageService,
    private readonly studentHelperService: StudentHelperService,
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
  ) {}

  public initializeMessageListener(): void {
    if (this.messageUnsubscribe) {
      console.warn('Message listener already initialized');
      return;
    }

    console.log('Initializing message listener');

    this.messageUnsubscribe = onMessage(this.messaging, (payload) => this.showCustomNotification(payload));

  }

  public requestPermission(): Observable<string | null> {
    return from(
      getToken(this.messaging, {
        vapidKey: environment.firebaseVapidKey,
      })
    ).pipe(
        first(),
        map((token) => {
          if (token) {
            console.log('FCM token obtained successfully');
            this.storageService.setFcmToken(token);
            this.initializeMessageListener();
            return token;
          } else {
            console.warn('NOTIFICATION_PERMISSION_DENIED');
            return null;
          }
        }),
        catchError((err) => {
          console.error('Error requesting FCM token:', err);
          this.storageService.removeFcmToken();
          return of(null);
        })
      );
  }

  public cleanup(): void {
    if (this.messageUnsubscribe) {
      console.log('Cleaning up message listener');
      this.messageUnsubscribe();
      this.messageUnsubscribe = null;
    }
  }

  public showCustomNotification(payload: MessagePayload): void {
    try {
      if (Notification.permission !== 'granted') {
        console.warn('Notification permission not granted');
        this.toastrService.info(
          this.translateService.instant('NOTIFICATION_RECEIVED') || 'Получено уведомление'
        );
        return;
      }

      console.info('Received foreground notification:', payload);

      const notificationData = (payload.data ?? {}) as unknown as Partial<PushNotificationData>;

      // Use direct Notification API for foreground (prevents FCM auto-show)
      const title = notificationData.title ?? this.translateService.instant('NEW_NOTIFICATION');

      const options: NotificationOptions = {
        body: notificationData.body,
        data: notificationData
      };

      // Use Service Worker Registration API for Android Chrome compatibility
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
          .then((registration) => {
            console.log('Showing notification via service worker registration');
            return registration.showNotification(title, options);
          })
          .catch((error) => {
            console.error('Error showing notification via service worker:', error);
            this.showToastrFallback(notificationData);
          });
      } else {
        console.warn('Service Worker not supported, falling back to toastr');
        this.showToastrFallback(notificationData);
      }

    } catch (error) {

      console.error('Error showing notification:', error);

      this.toastrService.info(
        payload.notification?.body || this.translateService.instant('NEW_NOTIFICATION') || 'Новое уведомление'
      );
    }
  }

  private showToastrFallback(data: Partial<PushNotificationData>): void {
    const message = data?.body ||
      this.translateService.instant('NEW_NOTIFICATION') ||
      'Новое уведомление';
    this.toastrService.info(message);
  }

  /**
   * @deprecated This method is no longer used since we switched to Service Worker Registration API.
   * The click handling is now done in firebase-messaging-sw.js.
   * Kept temporarily for reference and potential rollback.
   */
  private onNotificationClick(notificationData: Partial<PushNotificationData>): void  {
      console.info('Handling notification click with data:', notificationData);

      const role = this.authService.getRole();

      const shouldNavigateByRedirectUrl = !!notificationData.redirectUrl;

      const shouldNavigateToCuratorLesson = role === Roles.Curator &&
        !!notificationData.groupId &&
        !!notificationData.themeId &&
        !!notificationData.lessonId;

      const shouldNavigateToStudentLesson = role === Roles.Student &&
        !!notificationData.lessonId &&
        !!notificationData.lessonType &&
        !!notificationData.groupId;

      // Priority 1: if curator-lesson-related notification, navigate to curator lesson page
      if (shouldNavigateToCuratorLesson) {
        const curatorUrlParts = [
          BaseRoutes.CuratorNew,
          notificationData.groupId,
          notificationData.themeId,
          notificationData.lessonId
        ];

        const url = `/${curatorUrlParts.join('/')}`;

        this.ngZone.run(() => {
          this.router.navigateByUrl(url).catch((error) => {
            console.error('Navigation error:', error);
          });
        });

        return;
      }

      // Priority 2: if student-lesson-related notification, navigate to student lesson page
      if (shouldNavigateToStudentLesson) {
        const info: RedirectInfo = {
          id: notificationData.lessonId!,
          lessonType: notificationData.lessonType as LessonType,
        }

        const lessonUrlParts = [
          BaseRoutes.StudentNew,
          notificationData.groupId,
          this.studentHelperService.generateLessonsSiblingsRoute(info)
        ];

        const url = `/${lessonUrlParts.join('/')}`;

        this.ngZone.run(() => {
          this.router.navigateByUrl(url).catch((error) => {
            console.error('Navigation error:', error);
          });
        });

        return;
      }

      // else if there is redirectUrl, navigate to it
      if (shouldNavigateByRedirectUrl) {
        // If it's an absolute URL, use window.location, otherwise use router
        if (notificationData.redirectUrl!.startsWith('http')) {
          window.location.href = notificationData.redirectUrl!;
        } else {
          this.ngZone.run(() => {
            this.router.navigateByUrl(notificationData.redirectUrl!).catch((error) => {
              console.error('Navigation error:', error);
            });
          });
        }

        return;
      }

      // else if student, show student notifications page
      if (role === Roles.Student) {
        const url = `/${BaseRoutes.StudentNew}/${StudentRoutes.Notifications}`;

        this.ngZone.run(() => {
          this.router.navigateByUrl(url).catch((error) => {
            console.error('Navigation error:', error);
          });
        });

        return;
      }

      // else just stay on current page (already focused)
      console.log('No specific navigation action for this notification');
  }
}
